import React from 'react';
import { motion } from 'framer-motion';
import SegmentSection from '@/components/SegmentSection';
import InquiryForm from '@/components/InquiryForm';
import ProtagonistReel from '@/components/ProtagonistReel';
import PandiFullscreenReel from '@/components/PandiFullscreenReel';
import Moodboard from '@/components/Moodboard';
import BulletPassThrough from '@/components/BulletPassThrough';

// ---------- Helper sub-blocks ---------- //
const Pull = ({ children }) => (
  <blockquote className="mt-6 border-l-2 pl-5 py-1 italic font-display text-xl sm:text-2xl leading-snug gold-foil"
    style={{ borderColor: 'rgba(200,162,74,0.55)' }}>
    “{children}”
  </blockquote>
);

const Emphasis = ({ children }) => (
  <p className="mt-5 font-ui text-[12px] sm:text-[13px] tracking-[0.32em] uppercase text-[color:var(--gold)]">
    {children}
  </p>
);

const MetaGrid = ({ items }) => (
  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
    {items.map((m) => (
      <div key={m.k} className="flex flex-col">
        <span className="font-ui text-[10px] tracking-[0.28em] uppercase text-[color:var(--text-dim)]">{m.k}</span>
        <span className="font-display text-[15px] tracking-wide text-[color:var(--text-cream)] mt-1">{m.v}</span>
      </div>
    ))}
  </div>
);

const Pillars = ({ items }) => (
  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
    {items.map((p) => (
      <div key={p.k} className="card-smoky rounded-xl p-4">
        <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[color:var(--gold)]">{p.k}</div>
        <div className="mt-2 text-[color:var(--text-cream)] leading-snug">{p.v}</div>
      </div>
    ))}
  </div>
);

const BentoGrid = ({ items }) => (
  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
    {items.map((p) => (
      <div key={p.k} className="card-smoky rounded-xl p-4">
        <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[color:var(--text-dim)]">{p.k}</div>
        <div className="mt-1.5 text-[color:var(--text-cream)] leading-snug">{p.v}</div>
      </div>
    ))}
  </div>
);

const KVRows = ({ rows }) => (
  <div className="mt-6 divide-y divide-white/5 card-smoky rounded-xl overflow-hidden">
    {rows.map((r, i) => (
      <div key={i} className="grid grid-cols-3 gap-4 px-4 py-3">
        <div className="font-ui text-[11px] tracking-[0.28em] uppercase text-[color:var(--text-dim)] col-span-1">{r.a}</div>
        <div className="col-span-2 text-[color:var(--text-cream)]">{r.b}</div>
      </div>
    ))}
  </div>
);

// ---------- Per-segment renderer ---------- //
export default function SegmentRenderer({ data, index, segmentRef }) {
  // Special: cold-open hero (POSTER-CENTRIC, no 3D hologram here)
  if (data.id === 'cold-open') {
    return (
      <section
        id={data.id}
        ref={segmentRef}
        data-testid={data.testId}
        className="relative min-h-screen w-full flex items-center pt-28 pb-24"
      >
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left: copy block */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, ease: 'easeOut', delay: 0.15 }}
            >
              <div className="mb-5">
                <div className="font-display blood-text tracking-[0.34em] text-xs sm:text-sm">RAGHAVA LAWRENCE</div>
                <div className="mt-1 font-display tracking-[0.32em] text-[10px] sm:text-xs text-[color:var(--text-sand)]">IN AND AS</div>
              </div>
              <h1 className="leading-tight tracking-tight text-6xl sm:text-7xl md:text-[96px]">
                <BulletPassThrough pierceY={0.55} duration={0.65} interval={3.8} thickness={5}>
                  <span className="title-poster whitespace-nowrap">BLACK AXE</span>
                </BulletPassThrough>
              </h1>
              <div className="mt-5 font-display tracking-[0.28em] text-[11px] sm:text-xs text-[color:var(--text-sand)]">
                WRITTEN AND DIRECTED BY{' '}
                <span className="text-[color:var(--gold)]">ANL ARASU</span>
              </div>
              <p className="mt-7 max-w-[44ch] font-display text-xl sm:text-2xl leading-snug text-[color:var(--text-cream)]">
                “{data.logline}”
              </p>
              <p className="mt-5 max-w-[58ch] text-[color:var(--text-sand)] leading-relaxed">{data.body}</p>
              <p className="mt-7 font-ui text-[12px] tracking-[0.4em] uppercase blood-text">
                {data.emphasis}
              </p>
              <div className="mt-9 flex items-center gap-3">
                <a href="#contact-cta" className="btn-gold" data-testid="hero-cta-button">Take the Shot</a>
                <a href="#night-on-the-water" className="btn-ghost">Scroll the Deck</a>
              </div>
            </motion.div>
          </div>

          {/* Right: ACTUAL POSTER */}
          <div className="lg:col-span-5 order-1 lg:order-2 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.1, ease: 'easeOut' }}
              className="relative mx-auto max-w-[460px]"
            >
              <div className="relative overflow-hidden rounded-xl gold-border glow-blood">
                <BulletPassThrough pierceY={0.22} duration={0.85} interval={3.8} thickness={5}>
                  <img
                    src="/assets/poster.jpg"
                    alt="BLACK AXE — Official Poster"
                    className="block w-full h-auto select-none"
                    draggable={false}
                    data-testid="hero-poster"
                  />
                </BulletPassThrough>
                <div className="absolute inset-0 pointer-events-none bg-noise opacity-[0.05] mix-blend-overlay" />
                {/* Corner crosshairs */}
                <span className="absolute top-2 left-2 w-4 h-4 border-l border-t border-[color:var(--gold)]/70" />
                <span className="absolute top-2 right-2 w-4 h-4 border-r border-t border-[color:var(--gold)]/70" />
                <span className="absolute bottom-2 left-2 w-4 h-4 border-l border-b border-[color:var(--gold)]/70" />
                <span className="absolute bottom-2 right-2 w-4 h-4 border-r border-b border-[color:var(--gold)]/70" />
              </div>
              <div className="mt-3 flex items-center justify-between font-ui text-[10px] tracking-[0.32em] uppercase">
                <span className="text-[color:var(--text-dim)]">OFFICIAL POSTER · V1</span>
                <span className="text-[color:var(--gold)]">FIRST LOOK</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  // Closing poster reveal
  if (data.id === 'closing-poster') {
    return (
      <section
        id={data.id}
        ref={segmentRef}
        data-testid={data.testId}
        className="relative min-h-screen w-full flex items-center justify-center py-24"
      >
        <div className="mx-auto max-w-[820px] px-5 sm:px-8 w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: 'easeOut' }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="h-px w-12 bg-[color:var(--gold)]/60" />
              <span className="font-ui text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold)]">{data.location}</span>
              <span className="h-px w-12 bg-[color:var(--gold)]/60" />
            </div>
            <div className="relative mx-auto max-w-[520px]">
              <div className="relative overflow-hidden rounded-xl gold-border glow-blood">
                <BulletPassThrough pierceY={0.22} duration={0.85} interval={3.8} thickness={5}>
                  <img
                    src="/assets/poster.jpg"
                    alt="BLACK AXE — Official Poster"
                    className="block w-full h-auto select-none"
                    draggable={false}
                    data-testid="closing-poster"
                  />
                </BulletPassThrough>
                <div className="absolute inset-0 pointer-events-none bg-noise opacity-[0.05] mix-blend-overlay" />
                <span className="absolute top-2 left-2 w-4 h-4 border-l border-t border-[color:var(--gold)]/70" />
                <span className="absolute top-2 right-2 w-4 h-4 border-r border-t border-[color:var(--gold)]/70" />
                <span className="absolute bottom-2 left-2 w-4 h-4 border-l border-b border-[color:var(--gold)]/70" />
                <span className="absolute bottom-2 right-2 w-4 h-4 border-r border-b border-[color:var(--gold)]/70" />
              </div>
            </div>
            <p className="mt-8 font-display italic text-lg sm:text-xl text-[color:var(--text-cream)]">
              “{data.tagline}”
            </p>
            <p className="mt-6 font-ui text-[11px] tracking-[0.4em] uppercase blood-text">
              {data.emphasis}
            </p>
            <div className="mt-6 font-ui text-[10px] tracking-[0.32em] uppercase text-[color:var(--text-dim)]">
              © BLACK AXE — A Tamil Noir Feature · Written & Directed by Anl Arasu
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  if (data.id === 'contact-cta') {
    return (
      <section
        id={data.id}
        ref={segmentRef}
        data-testid={data.testId}
        className="relative min-h-screen w-full flex items-center py-24"
      >
        <div className="mx-auto max-w-[980px] px-5 sm:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            viewport={{ once: true, amount: 0.3 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px w-12 bg-[color:var(--gold)]/60" />
              <span className="font-ui text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold)]">{data.location}</span>
              <span className="h-px w-12 bg-[color:var(--gold)]/60" />
            </div>
            <h2 className="font-deco text-5xl sm:text-6xl md:text-7xl">
              <span className="title-poster">{data.title}</span>
            </h2>
            <p className="mt-6 max-w-[60ch] mx-auto text-[color:var(--text-sand)] leading-relaxed">{data.body}</p>
          </motion.div>
          <div className="mt-10 max-w-[760px] mx-auto card-smoky rounded-2xl p-6 sm:p-8 gold-border glow-gold">
            <InquiryForm />
          </div>
        </div>
      </section>
    );
  }

  if (data.id === 'protagonist-reel') {
    // FULLSCREEN cinematic reel — 16:9 master video plays with audio the
    // moment the user scrolls Pandi's introduction into view. Text rail
    // sits ABOVE the player so the footage owns the full width below.
    return (
      <section
        id={data.id}
        ref={segmentRef}
        data-testid={data.testId}
        className="relative min-h-screen w-full py-20 sm:py-24"
      >
        <div className="mx-auto max-w-[1480px] px-5 sm:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            viewport={{ once: true, amount: 0.2 }}
            className="max-w-[860px]"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="font-ui text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold)]">
                CH {data.number}
              </span>
              <span className="h-px w-10 bg-[color:var(--gold)]/60" />
              <span className="font-ui text-[10px] tracking-[0.32em] uppercase text-[color:var(--text-dim)]">
                {data.location}
              </span>
            </div>
            <h2 className="font-deco text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight title-deco">
              <span className="title-poster">{data.title}</span>
            </h2>
            <p className="mt-6 max-w-[64ch] text-[color:var(--text-sand)] text-base sm:text-[17px] leading-relaxed">
              {data.body}
            </p>
          </motion.div>

          {/* 16:9 FULLSCREEN cinematic player — autoplays with audio on view */}
          <div className="mt-10 sm:mt-12">
            <PandiFullscreenReel />
          </div>
        </div>
      </section>
    );
  }

  // THE POLICE — same cinematic 2.39:1 moodboard treatment, anchored
  // immediately after AKR EMPIRE.
  if (data.id === 'police') {
    return (
      <section
        id={data.id}
        ref={segmentRef}
        data-testid={data.testId}
        className="relative min-h-screen w-full py-24 sm:py-28"
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            viewport={{ once: true, amount: 0.2 }}
            className="max-w-[680px]"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="font-ui text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold)]">CH {data.number}</span>
              <span className="h-px w-10 bg-[color:var(--gold)]/60" />
              <span className="font-ui text-[10px] tracking-[0.32em] uppercase text-[color:var(--text-dim)]">{data.location}</span>
            </div>
            <h2 className="font-deco text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight title-deco">
              <span className="title-poster">{data.title}</span>
            </h2>
            {data.body.split('\n\n').map((para, i) => (
              <p key={i} className="mt-6 text-[color:var(--text-sand)] leading-relaxed">{para}</p>
            ))}
          </motion.div>
          <Moodboard tiles={data.moodTiles} />
          {data.pull && <Pull>{data.pull}</Pull>}
        </div>
      </section>
    );
  }

  if (data.id === 'moodboard' || data.id === 'world-moodboard') {
    return (
      <section
        id={data.id}
        ref={segmentRef}
        data-testid={data.testId}
        className="relative min-h-screen w-full py-24 sm:py-28"
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            viewport={{ once: true, amount: 0.2 }}
            className="max-w-[640px]"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="font-ui text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold)]">CH {data.number}</span>
              <span className="h-px w-10 bg-[color:var(--gold)]/60" />
              <span className="font-ui text-[10px] tracking-[0.32em] uppercase text-[color:var(--text-dim)]">{data.location}</span>
            </div>
            <h2 className="font-deco text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight title-deco">
              <span className="title-poster">{data.title}</span>
            </h2>
            <p className="mt-6 text-[color:var(--text-sand)] leading-relaxed">{data.body}</p>
          </motion.div>
          <Moodboard tiles={data.moodTiles} />
        </div>
      </section>
    );
  }

  // Logline — hero-styled, single oversized statement
  if (data.id === 'logline') {
    return (
      <section
        id={data.id}
        ref={segmentRef}
        data-testid={data.testId}
        className="relative min-h-screen w-full flex items-center py-24"
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 w-full grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-start-1 md:col-end-9 relative z-20">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="font-ui text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold)]">CH {data.number}</span>
                <span className="h-px w-12 bg-[color:var(--gold)]/60" />
                <span className="font-ui text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold)]">{data.location}</span>
              </div>
              <h2 className="font-deco text-2xl sm:text-3xl md:text-4xl leading-tight title-deco">
                <span className="title-poster">{data.title}</span>
              </h2>
              <p
                data-testid="logline-text"
                className="mt-8 max-w-[44ch] font-display italic text-2xl sm:text-3xl md:text-[40px] leading-[1.2] gold-foil tracking-tight"
              >
                “{data.logline}”
              </p>
              <p className="mt-8 font-ui text-[11px] tracking-[0.4em] uppercase blood-text">
                BLACK AXE · A TAMIL NOIR FEATURE
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  // Synopsis — multi-paragraph long-form story block
  if (data.id === 'synopsis') {
    return (
      <SegmentSection data={data} index={index} segmentRef={segmentRef}>
        <div data-testid="synopsis-paragraphs" className="space-y-5">
          {data.paragraphs.map((para, i) => (
            <p key={i} className="text-[color:var(--text-sand)] leading-relaxed">
              {para}
            </p>
          ))}
        </div>
        {data.pull && <Pull>{data.pull}</Pull>}
      </SegmentSection>
    );
  }

  // DIRECTOR CREDITS — final end-card style section with director photo,
  // bio paragraphs, filmography rows, and a closing "A FILM BY ANL ARASU"
  // card. Lives as the very last segment of the deck.
  if (data.id === 'director-credits') {
    return (
      <section
        id={data.id}
        ref={segmentRef}
        data-testid={data.testId}
        className="relative min-h-screen w-full py-28 sm:py-32"
      >
        <div className="mx-auto max-w-[1480px] px-5 sm:px-10">
          {/* Pre-title strip */}
          <div className="flex items-center gap-4 mb-8">
            <span className="font-ui text-[11px] tracking-[0.45em] uppercase text-[color:var(--gold)]">CH {data.number}</span>
            <span className="h-px w-14 bg-[color:var(--gold)]/70" />
            <span className="font-ui text-[11px] tracking-[0.45em] uppercase text-[color:var(--gold)]">{data.location}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            {/* Director portrait — bigger column */}
            <motion.div
              className="md:col-span-5 lg:col-span-5 relative"
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden gold-border shadow-[0_30px_60px_rgba(0,0,0,0.6)]">
                <img
                  src={data.image}
                  alt={data.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/0 to-transparent pointer-events-none" />
                <div className="absolute inset-0 bg-noise opacity-[0.05] mix-blend-overlay pointer-events-none" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <div className="font-ui text-[11px] sm:text-[12px] tracking-[0.45em] uppercase text-[color:var(--gold)]">{data.tagline}</div>
                </div>
                <span className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 border-[color:var(--gold)]/80" />
                <span className="absolute top-4 right-4 w-6 h-6 border-r-2 border-t-2 border-[color:var(--gold)]/80" />
                <span className="absolute bottom-4 left-4 w-6 h-6 border-l-2 border-b-2 border-[color:var(--gold)]/80" />
                <span className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 border-[color:var(--gold)]/80" />
              </div>
            </motion.div>

            {/* Bio + meta + filmography — right column */}
            <motion.div
              className="md:col-span-7 lg:col-span-7"
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.1 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <h2 className="font-deco leading-[0.95] tracking-tight title-deco text-[clamp(56px,8vw,128px)]">
                <span className="title-poster">{data.title}</span>
              </h2>

              {/* Meta strip */}
              {data.meta && (
                <div className="mt-8 grid grid-cols-3 gap-4">
                  {data.meta.map((m) => (
                    <div key={m.k} className="rounded-lg border border-[color:var(--gold)]/30 bg-black/35 px-4 py-3">
                      <div className="font-ui text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold)]">{m.k}</div>
                      <div className="mt-1.5 font-display text-[15px] sm:text-[17px] text-[color:var(--text-cream)] leading-tight">{m.v}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Bio paragraphs */}
              {data.paragraphs && (
                <div className="mt-9 space-y-5 max-w-[62ch]">
                  {data.paragraphs.map((p, i) => (
                    <p key={i} className="text-[17px] sm:text-[18px] text-[color:var(--text-sand)] leading-relaxed">{p}</p>
                  ))}
                </div>
              )}

              {/* Filmography */}
              {data.filmography && (
                <div className="mt-12">
                  <div className="flex items-center gap-4 mb-5">
                    <span className="h-px w-10 bg-[color:var(--gold)]/70" />
                    <span className="font-ui text-[11px] tracking-[0.5em] uppercase text-[color:var(--gold)]">
                      SELECTED FILMOGRAPHY
                    </span>
                    <span className="h-px w-10 bg-[color:var(--gold)]/70" />
                  </div>
                  <ul data-testid="director-filmography" className="divide-y divide-[color:var(--gold)]/20 border-y border-[color:var(--gold)]/20">
                    {data.filmography.map((f) => (
                      <li key={f.k} className="flex items-center justify-between py-4">
                        <span className="font-deco text-2xl sm:text-3xl text-[color:var(--text-cream)] title-deco">
                          {f.k}
                        </span>
                        <span className="font-ui text-[11px] tracking-[0.4em] uppercase text-[color:var(--gold)]">
                          {f.v}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          </div>

          {/* FINAL CARD */}
          {data.finalCard && (
            <motion.div
              data-testid="director-final-card"
              className="mt-24 sm:mt-28 text-center"
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="font-ui text-[11px] tracking-[0.55em] uppercase text-[color:var(--text-dim)]">
                {data.finalCard.pre}
              </div>
              <div className="mt-5 font-deco title-deco text-[clamp(64px,9vw,160px)] leading-[0.9]">
                <span className="title-poster">{data.finalCard.name}</span>
              </div>
              <div className="mt-6 font-ui text-[12px] tracking-[0.5em] uppercase text-[color:var(--gold)]/95">
                {data.finalCard.sub}
              </div>
              <div className="mt-10 mx-auto h-px w-32 bg-[color:var(--gold)]/70" />
            </motion.div>
          )}
        </div>
      </section>
    );
  }

  // THE DIRECTOR — profile + future vision block (separate heading after
  // the body bio so the producer / investor reads it as a distinct beat).
  if (data.id === 'the-director') {
    return (
      <SegmentSection data={data} index={index} segmentRef={segmentRef}>
        <p>{data.body}</p>
        {data.meta && <MetaGrid items={data.meta} />}
        {data.futureVision && data.futureVision.length > 0 && (
          <div
            data-testid="director-future-vision"
            className="mt-10 border-l-2 border-[color:var(--gold)]/70 pl-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
              <span className="font-ui text-[10px] tracking-[0.45em] uppercase text-[color:var(--gold)]">
                FUTURE VISION
              </span>
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
            </div>
            {data.futureVision.map((line, i) => (
              <p
                key={i}
                className="font-display italic text-lg sm:text-xl leading-[1.45] text-[color:var(--text-cream)] mb-3 last:mb-0"
              >
                {line}
              </p>
            ))}
          </div>
        )}
      </SegmentSection>
    );
  }

  // PANDI — character profile + full-width 2.39:1 plates underneath.
  if (data.id === 'pandi' || data.id === 'directors-vision') {
    return (
      <>
        <SegmentSection data={data} index={index} segmentRef={segmentRef}>
          {data.body.split('\n\n').map((para, i) => (
            <p key={i} className={i > 0 ? 'mt-5' : undefined}>{para}</p>
          ))}
          {data.meta && <MetaGrid items={data.meta} />}
          {data.pillars && <Pillars items={data.pillars} />}
        </SegmentSection>
        {data.moodTiles && data.moodTiles.length > 0 && (
          <div
            className="relative w-full pb-24 sm:pb-28 -mt-24 sm:-mt-28"
            data-testid={`${data.id}-plates`}
          >
            <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
              <Moodboard tiles={data.moodTiles} />
            </div>
          </div>
        )}
      </>
    );
  }

  // THE HIDDEN FEW + THE FISHERMAN & THE GIRL — default cinematic layout
  // plus full-width 2.39:1 plates underneath. Plates live OUTSIDE
  // SegmentSection so they can break out of the narrow text column.
  if (data.id === 'hidden-few' || data.id === 'fisherman-girl') {
    return (
      <>
        <SegmentSection data={data} index={index} segmentRef={segmentRef}>
          <p>{data.body}</p>
          {data.pull && <Pull>{data.pull}</Pull>}
          {data.pillars && <Pillars items={data.pillars} />}
        </SegmentSection>
        {data.moodTiles && data.moodTiles.length > 0 && (
          <div
            className="relative w-full pb-24 sm:pb-28 -mt-24 sm:-mt-28"
            data-testid={`${data.id}-plates`}
          >
            <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
              <Moodboard tiles={data.moodTiles} />
            </div>
          </div>
        )}
      </>
    );
  }

  // Default cinematic segment
  return (
    <SegmentSection data={data} index={index} segmentRef={segmentRef}>
      <p>{data.body}</p>
      {data.pull && <Pull>{data.pull}</Pull>}
      {data.emphasis && <Emphasis>{data.emphasis}</Emphasis>}
      {data.meta && <MetaGrid items={data.meta} />}
      {data.pillars && <Pillars items={data.pillars} />}
      {data.grid && <BentoGrid items={data.grid} />}
      {data.rows && <KVRows rows={data.rows} />}
    </SegmentSection>
  );
}
